(function () {
  if (window.MRCartMutationCoordinator) return;

  var queueTail = Promise.resolve();
  var nextAllowedAt = 0;
  var rateLimitBlocked = false;
  var rateLimitMessage = 'To protect your cart from repeated automated requests, please refresh the page to verify your request, then try again.';

  function wait(milliseconds) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, Math.max(0, milliseconds || 0));
    });
  }

  function waitForRequestSlot() {
    return wait(nextAllowedAt - Date.now());
  }

  function readResponsePayload(response) {
    return response.text().then(function (text) {
      if (!text) return null;
      try {
        return JSON.parse(text);
      } catch (error) {
        return text;
      }
    });
  }

  function payloadMessage(payload, fallback) {
    if (payload && typeof payload === 'object') {
      var message = payload.description || payload.message || payload.errors;
      if (typeof message === 'string' && message.trim()) return message;
      if (message && typeof message === 'object') {
        try {
          return JSON.stringify(message);
        } catch (error) {
          // Fall through to the stable message below.
        }
      }
    }
    if (typeof payload === 'string' && payload.trim()) return payload;
    return fallback || 'Unable to update the cart.';
  }

  function retryAfterMilliseconds(response) {
    var value = response && response.headers && response.headers.get('Retry-After');
    if (!value) return 0;

    var seconds = Number(value);
    if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);

    var date = Date.parse(value);
    return Number.isFinite(date) ? Math.max(0, date - Date.now()) : 0;
  }

  function CartMutationError(message, details) {
    this.name = 'CartMutationError';
    this.message = message || 'Unable to update the cart.';
    this.status = Number(details && details.status) || 0;
    this.payload = details && details.payload;
    this.response = details && details.response;
    this.cause = details && details.cause;
    if (Error.captureStackTrace) Error.captureStackTrace(this, CartMutationError);
  }

  CartMutationError.prototype = Object.create(Error.prototype);
  CartMutationError.prototype.constructor = CartMutationError;

  function runMutation(fetcher, options) {
    var settings = options || {};
    var cooldownMilliseconds = Math.max(0, Number(settings.cooldownMs) || 1200);
    var maximumRetries = Math.max(0, Number(settings.maxRetries) || 0);
    var retryStatuses = Array.isArray(settings.retryStatuses) ? settings.retryStatuses : [409];
    var attempt = 0;

    return (async function () {
      while (true) {
        if (rateLimitBlocked) {
          throw new CartMutationError(rateLimitMessage, { status: 429 });
        }

        await waitForRequestSlot();

        var response;
        try {
          response = await fetcher({ attempt: attempt });
        } catch (cause) {
          nextAllowedAt = Math.max(nextAllowedAt, Date.now() + 400);
          throw new CartMutationError('Unable to reach the cart. Please try again.', {
            status: 0,
            cause: cause
          });
        }

        var payload = await readResponsePayload(response);
        var result = {
          ok: response.ok,
          status: response.status,
          payload: payload,
          response: response,
          attempt: attempt
        };

        if (response.ok) {
          nextAllowedAt = Math.max(nextAllowedAt, Date.now() + cooldownMilliseconds);
          return result;
        }

        var canRetry = attempt < maximumRetries && retryStatuses.indexOf(response.status) !== -1;
        if (canRetry) {
          var fallbackDelay = response.status === 429 ? 2500 : 800;
          var retryDelay = Math.max(retryAfterMilliseconds(response), fallbackDelay);
          nextAllowedAt = Math.max(nextAllowedAt, Date.now() + retryDelay);

          if (typeof settings.onRetry === 'function') {
            try {
              await settings.onRetry({
                status: response.status,
                delay: retryDelay,
                attempt: attempt + 1,
                payload: payload
              });
            } catch (callbackError) {
              // A presentation callback must never break the cart queue.
            }
          }

          attempt += 1;
          continue;
        }

        if (response.status === 429) rateLimitBlocked = true;

        var failureDelay = response.status === 429 ? 0 : cooldownMilliseconds;
        nextAllowedAt = Math.max(nextAllowedAt, Date.now() + failureDelay);

        throw new CartMutationError(response.status === 429 ? rateLimitMessage : payloadMessage(payload), {
          status: response.status,
          payload: payload,
          response: response
        });
      }
    })();
  }

  function mutate(fetcher, options) {
    if (typeof fetcher !== 'function') {
      return Promise.reject(new TypeError('A cart mutation fetcher is required.'));
    }

    var queued = queueTail.then(
      function () { return runMutation(fetcher, options); },
      function () { return runMutation(fetcher, options); }
    );
    queueTail = queued.then(
      function () {},
      function () {}
    );
    return queued;
  }

  window.MRCartMutationCoordinator = {
    mutate: mutate,
    CartMutationError: CartMutationError,
    payloadMessage: payloadMessage,
    isRateLimitBlocked: function () { return rateLimitBlocked; }
  };
})();
